import { useEffect } from 'react';
import {
  Button,
  FormCheckbox,
  FormSelect,
  NumericFormatInput,
  Icon,
  trackEvent,
} from '@requity-homes/component-library';
import React from 'react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { listingCities } from '@requity-homes/utils';
import { MAXIMUM_HOME_PRICE, MINIMUM_HOME_PRICE } from '../../const';
import { queryParamsToCityMap } from '../../utils';

export interface FilterPanelProps {
  title: string;
  toggleFilters: () => void;
  updateFilters: (values: FilterPanelFormValues) => void;
  values: Partial<FilterPanelFormValues>;
}

export interface FilterPanelFormValues {
  boundary?: any;
  city: string;
  minPrice: string;
  maxPrice: string;
  minBeds: string;
  minBaths: string;
  sqft: string;
  yearBuilt: string;
  hasImages: boolean;
  maxMonthlyPayment: string | number;
}

enum PriceType {
  HOME_PRICE = 'home-price',
  MONTHLY_PAYMENT = 'monthly-payment',
}

function yupTransformNumeric(_, value) {
  if (typeof value === 'string') {
    return +value?.replace(/,/g, '');
  }

  return value;
}

export function FilterPanel({
  title,
  toggleFilters,
  updateFilters,
  values,
}: FilterPanelProps) {
  const [filterPrice, setFilterPrice] = React.useState<string>('');
  const [rangeValue, setRangeValue] = React.useState(`${MINIMUM_HOME_PRICE}`);
  const [monthlyPaymentRangeValue, setMonthlyPaymentRangeValue] =
    React.useState(`${1380}`);
  const [selectedPriceType, setSelectedPriceType] =
    React.useState<PriceType>(null);

  const initialValues: FilterPanelFormValues = {
    minPrice: `${MINIMUM_HOME_PRICE}`,
    maxPrice: '',
    minBeds: '',
    maxMonthlyPayment: '',
    minBaths: '',
    sqft: '',
    yearBuilt: '',
    hasImages: true,
    ...values,
    city: queryParamsToCityMap[values.city] || '',
  };

  const validationSchema = yup.object({
    city: yup.string().required('Please provide a name.'),
    maxPrice: yup
      .number()
      .transform(yupTransformNumeric)
      .min(MINIMUM_HOME_PRICE, 'Minimum price cannot be lower than $150,000')
      .max(MAXIMUM_HOME_PRICE, 'Maximum price cannot exceed $600,000'),
    minBeds: yup.string(),
    minBaths: yup.string(),
    sqft: yup.string(),
    yearBuilt: yup.string(),
    hasImages: yup.boolean(),
  });

  useEffect(() => {
    if (values.minPrice || values.maxPrice) {
      setFilterPrice(PriceType.HOME_PRICE);
    }
  }, [values]);

  const removeAnyPreviousPriceValue = (values) => {
    if (filterPrice === '') {
      delete values.minPrice;
      delete values.maxPrice;
    }

    if (selectedPriceType === PriceType.HOME_PRICE || filterPrice === '') {
      delete values.maxMonthlyPayment;
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4 p-6 pb-0">
        <h3 className="text-xl font-semibold">{title}</h3>
        <button
          className="px-1 pb-1 hover:bg-coral-light rounded"
          onClick={toggleFilters}
          aria-label="Close filters"
        >
          <Icon glyph="close" />
        </button>
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          delete values.boundary;
          const clonedValues = { ...values };

          if (selectedPriceType === PriceType.MONTHLY_PAYMENT) {
            const calculatedHomePrice =
              (Number(clonedValues.maxMonthlyPayment) / 0.92) * 100;
            clonedValues.maxPrice = Math.ceil(calculatedHomePrice).toString();
            delete clonedValues.maxMonthlyPayment;
          }

          trackEvent('Listing-SearchFilter', {
            city: clonedValues.city,
            minPrice: clonedValues.minPrice,
            maxPrice: clonedValues.maxPrice,
          });
          removeAnyPreviousPriceValue(clonedValues);

          updateFilters(clonedValues);
          toggleFilters();
        }}
      >
        {() => (
          <Form>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <div className="mb-4">
                <FormSelect label="Select cities" name="city">
                  {Object.keys(listingCities).map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </FormSelect>
              </div>
              <div className="mb-4">
                <FormSelect
                  name="filterPrice"
                  label="Filter price by"
                  className="first:text-grey-1"
                  onChange={(event) => {
                    if (event.target.value === PriceType.HOME_PRICE) {
                      setSelectedPriceType(PriceType.HOME_PRICE);
                    } else {
                      setSelectedPriceType(PriceType.MONTHLY_PAYMENT);
                    }
                    setFilterPrice(event.target.value);
                  }}
                >
                  <option
                    value=""
                    selected={filterPrice !== PriceType.HOME_PRICE}
                  >
                    None
                  </option>
                  <option
                    value={PriceType.HOME_PRICE}
                    selected={filterPrice === PriceType.HOME_PRICE}
                  >
                    List price
                  </option>
                  <option
                    value={PriceType.MONTHLY_PAYMENT}
                    selected={filterPrice === PriceType.MONTHLY_PAYMENT}
                  >
                    Monthly Payment
                  </option>
                </FormSelect>
              </div>
              {filterPrice === PriceType.HOME_PRICE && (
                <div className="mb-4">
                  <NumericFormatInput
                    name="maxPrice"
                    label="List price"
                    defaultValue={rangeValue}
                    handleValueChange={setRangeValue}
                  />
                  <div className="flex flex-col gap-0 my-2">
                    <input
                      type="range"
                      min={`${MINIMUM_HOME_PRICE}`}
                      max={`${MAXIMUM_HOME_PRICE}`}
                      className="w-full my-2 bg-coral-light h-2 rounded-lg rangeSlider"
                      value={rangeValue}
                      onChange={(e) => setRangeValue(e.target.value)}
                    />
                    <div className="flex justify-between">
                      <span>${MINIMUM_HOME_PRICE}</span>
                      <span>${MAXIMUM_HOME_PRICE}</span>
                    </div>
                  </div>
                </div>
              )}
              {filterPrice === PriceType.MONTHLY_PAYMENT && (
                <div className="mb-4">
                  <NumericFormatInput
                    name="maxMonthlyPayment"
                    label="Max monthly payment"
                    defaultValue={monthlyPaymentRangeValue}
                    handleValueChange={setMonthlyPaymentRangeValue}
                  />
                  <div className="flex flex-col gap-0 my-2">
                    <input
                      type="range"
                      min={`${1380}`}
                      max={`${5500}`}
                      className="w-full my-2 bg-coral-light h-2 rounded-lg rangeSlider"
                      value={monthlyPaymentRangeValue}
                      onChange={(e) =>
                        setMonthlyPaymentRangeValue(e.target.value)
                      }
                    />
                    <div className="flex justify-between">
                      <span>${1380}</span>
                      <span>${5500}</span>
                    </div>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <FormSelect label="Select beds" name="minBeds">
                    <option value="" selected>
                      No min
                    </option>
                    <option value="1">1+</option>
                    <option value="2">2+</option>
                    <option value="3">3+</option>
                    <option value="4">4+</option>
                    <option value="5">5+</option>
                    <option value="6">6+</option>
                  </FormSelect>
                </div>
                <div>
                  <FormSelect label="Select baths" name="minBaths">
                    <option value="" selected>
                      No min
                    </option>
                    <option value="1">1+</option>
                    <option value="2">2+</option>
                    <option value="3">3+</option>
                    <option value="4">4+</option>
                    <option value="5">5+</option>
                    <option value="6">6+</option>
                  </FormSelect>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <FormSelect label="Select square feet" name="sqft">
                    <option value="" selected>
                      No min
                    </option>
                    <option value="500">500+</option>
                    <option value="750">750+</option>
                    <option value="1000">1000+</option>
                    <option value="1250">1250+</option>
                    <option value="1500">1500+</option>
                    <option value="1750">1750+</option>
                    <option value="2000">2000+</option>
                    <option value="2250">2250+</option>
                    <option value="2500">2500+</option>
                    <option value="2750">2750+</option>
                    <option value="3000">3000+</option>
                    <option value="3500">3500+</option>
                    <option value="4000">4000+</option>
                    <option value="5000">5000+</option>
                    <option value="7500">7500+</option>
                  </FormSelect>
                </div>
                <div>
                  <NumericFormatInput
                    name="yearBuilt"
                    label={'Year built'}
                    placeholder="Min year"
                    disableThousandSeparator
                  />
                </div>
              </div>
              <div className="flex items-center mb-4">
                <FormCheckbox
                  label="Hide homes with no photos"
                  name={'hasImages'}
                />
              </div>
              <div className="mb-4">
                <FormSelect label="Sort by" name="sortBy">
                  <option value="recommended">Recommended</option>
                  <option value="listPriceAsc">Price (Low to High)</option>
                  <option value="listPriceDesc">Price (High to Low)</option>
                  <option value="createdOnDesc">Newest</option>
                </FormSelect>
              </div>
              <div className="flex justify-between">
                <Button
                  className="w-full mr-2 px-0 sm:px-0 bg-white active:bg-white shadow-md-dark text-coral-med focus:outline-none border-transparent"
                  aria-label="Reset Filters"
                >
                  Reset
                </Button>
                <Button
                  className="mx-0 w-full ml-2 px-0 sm:px-0 bg-white active:bg-white shadow-md-dark text-coral-med focus:outline-none border-transparent"
                  type="submit"
                  aria-label="See Filtered Results"
                >
                  See results
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
